import React, { useState, useRef } from 'react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { Form, Formik, FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import {
  useTranslator,
  FormikInput as Input,
  useToast,
  useAppContext,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import validationSchema from './validationSchema';
import { withForcedAiError } from '../helpers/devForcedError';

interface DescribeActivityContainerProps {
  estimateBurnedKcals: (input: any) => Promise<any>;
  cancelEstimateBurnedKcals: () => void;
}

const DescribeActivityContainer: React.FC<DescribeActivityContainerProps> = ({
  estimateBurnedKcals,
  cancelEstimateBurnedKcals,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const navigate = useNavigate();
  const isCanceledRef = useRef(false);

  const {
    images: { aiStarDarkIcon, aiStarLightIcon, microphonePrimaryIcon },
  } = useAppContext();

  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [noActivityError, setNoActivityError] = useState(false);

  const formikValues = {
    description: '',
  };

  const handleSubmit = async (
    values: typeof formikValues,
    { setSubmitting }: FormikHelpers<typeof formikValues>
  ) => {
    setRateLimitExceeded(false);
    setNoActivityError(false);

    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const response = withForcedAiError(
        await estimateBurnedKcals({
          activityDescription: values.description,
        })
      );

      if (response.__typename === 'EstimatedActivity') {
        navigate('activity-details', {
          state: { activityData: response },
        });
        setSubmitting(false);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
        setSubmitting(false);
      } else if (response.__typename === 'NotPhysicalActivityError') {
        setNoActivityError(true);
        setSubmitting(false);
      } else {
        toastError();
        setSubmitting(false);
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      // For other errors, reset submitting state and show error
      setSubmitting(false);
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.describeActivity.navbar.title')}</TitleBar>

      <Formik
        onSubmit={handleSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ isValid, values, isSubmitting, setSubmitting }) => {
          const handleCancel = () => {
            // Set flag to prevent Formik from retrying the submit
            isCanceledRef.current = true;
            // Cancel the ongoing GraphQL mutation
            cancelEstimateBurnedKcals();
            // Reset submitting state immediately
            setSubmitting(false);
            // Reset flag to allow retry
            setTimeout(() => {
              isCanceledRef.current = false;
            }, 0);
          };

          return (
            <Form>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col md={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <h2 className="title-medium semi-bold">
                            {t('tracker.describeActivity.title')}
                          </h2>
                          <p className="small text-text-variant-color">
                            {t('tracker.describeActivity.description')}
                          </p>
                          <Input
                            name="description"
                            type="textarea"
                            placeholder={t('tracker.describeActivity.input.placeholder')}
                            required
                            translateErrMsg
                          />

                          <div className="d-flex gap-2">
                            <p className="small medium">
                              {t('tracker.describeItem.example.title')}
                            </p>
                            <p className="small text-text-variant-color">
                              {t('tracker.describeActivity.example.content')}
                            </p>
                          </div>

                          {noActivityError && (
                            <Alert color="warning" className="mt-xl mb-0" fade={false}>
                              <p className="mb-0">
                                {t('tracker.describeActivity.analyze.noActivity.notification')}
                              </p>
                            </Alert>
                          )}

                          {rateLimitExceeded && (
                            <Alert color="danger" className="mt-xl mb-0" fade={false}>
                              <p className="mb-0">{t('tracker.limitUsageError.notification')}</p>
                            </Alert>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    {!isSubmitting ? (
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          <Button
                            type="button"
                            tag={Link}
                            to="/tracker/describe-activity/by-voice"
                            color="secondary"
                            outline
                            className="w-100 mb-s"
                            replace
                          >
                            <img
                              src={microphonePrimaryIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.describeItem.button.byVoice')}
                          </Button>

                          <Button
                            type="submit"
                            color="primary"
                            disabled={!isValid}
                            className="w-100"
                          >
                            <img
                              src={aiStarLightIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.describeActivity.button.analyze')}
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12" className="text-center">
                          <p className="description text-text-variant-color2">
                            {t('tracker.describeActivity.loader.analyzing')}
                          </p>
                        </Col>
                        <Col xs="4">
                          <Button type="button" color="link" onClick={handleCancel}>
                            {t('tracker.describeFood.loader.cancelButton')}
                          </Button>
                        </Col>
                        <Col xs="4">
                          <div className="ai-star">
                            <img src={aiStarDarkIcon} alt="" />
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default DescribeActivityContainer;
